import { Link, useLocation, useNavigate } from "react-router-dom";

import { useLogout, useSession, useSwitchView } from "@/api/queries";
import { cn } from "@/lib/cn";
import { initialOf, roleLabel, viewModeLabel } from "@/lib/session";
import { useUiStore } from "@/store/ui";
import { PLATFORM_NAV } from "@/views/meta";

/**
 * 平台头部（原型 `.platform-header`，68px）。
 * 观众侧视图为暗色主题，工作台视图下由 `body.is-studio-view` 切换到浅色。
 *
 * 身份切换入口在本处（`ADR-025` D6）：切身份是**高频**动作（编剧要反复看"玩家眼里的这条线"），
 * 放在 `/account` 里等于让它多一次点击。
 */
export function PlatformHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const logout = useLogout();
  const switchView = useSwitchView();
  const pushToast = useUiStore((state) => state.pushToast);
  const user = session.data ?? null;
  const onLoginPage = location.pathname === "/login";
  const activeView = location.pathname.startsWith("/studio")
    ? "creator"
    : location.pathname === "/discover"
      ? "discover"
      : location.pathname === "/community"
        ? "community"
        : "home";

  // 只有**已获授权**的账号才给"切到编剧"的入口；未授权时后端会 403，
  // 这里不给按钮只是"不提供无效入口"，不是权限边界（CONSTITUTION A3）。
  const canSwitchToCreator = user?.platform_role === "editor";
  const nextView = user?.view_mode === "editor" ? "player" : "editor";

  return (
    <header className="platform-header" aria-label="平台导航">
      <Link className="platform-brand" to="/" aria-label="返回未必首页">
        <span className="brand-mark" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
        <span className="platform-brand-copy">
          <strong>未必</strong>
          <small>OTHERWISE</small>
        </span>
      </Link>

      <nav className="platform-nav" aria-label="平台一级入口">
        {PLATFORM_NAV.map((item) => {
          const isActive = item.view === activeView;
          return (
            <Link
              key={item.view}
              to={item.to}
              className={cn("platform-nav-item", isActive && "is-active")}
              aria-current={isActive ? "page" : undefined}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="platform-actions">
        {onLoginPage ? null : user ? (
          <>
            {/* 当前身份（不是授权）：观众 ⇄ 编剧（`ADR-025` D1）。 */}
            <span className="platform-role">
              <i></i> {viewModeLabel(user.view_mode)}身份
            </span>
            {canSwitchToCreator ? (
              <button
                type="button"
                className="platform-role-switch"
                disabled={switchView.isPending}
                title={`切到${viewModeLabel(nextView)}身份（不会改变你的权限）`}
                // ⚠️ 与 `AccountView` 同一处纪律：`useSwitchView` 声明了 `errorHandled: true`
                // （公共兜底跳过），所以本调用点**必须**自呈现失败，否则失败无声无息。
                onClick={() =>
                  switchView.mutate(nextView, {
                    onSuccess: (updated) =>
                      pushToast({
                        title: "已切换身份",
                        detail: `现以${viewModeLabel(updated.view_mode)}身份浏览`,
                        mark: "✓",
                      }),
                    onError: (error) =>
                      pushToast({
                        title: "切换失败",
                        detail: error instanceof Error ? error.message : "未知错误",
                        mark: "!",
                      }),
                  })
                }
              >
                切到{viewModeLabel(nextView)}
              </button>
            ) : null}
            <Link className="platform-nav-item" to="/account">
              账号设置
            </Link>
            <button
              className="platform-profile"
              type="button"
              title="退出登录"
              disabled={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSuccess: () => navigate("/") })}
            >
              <span className="avatar avatar-founder">{initialOf(user)}</span>
              <span>
                <strong>{user.display_name || user.email}</strong>
                <small>{roleLabel(user)} · 退出登录</small>
              </span>
            </button>
          </>
        ) : (
          <>
            <Link className="platform-nav-item" to="/account">
              账号设置
            </Link>
            <Link className="platform-nav-item" to="/login">
              登录 / 注册
            </Link>
          </>
        )}
      </div>
    </header>
  );
}
